import api from './api';

// Use the main API instance which has CSRF token handling built-in
const API = api;

// Note: CSRF token is now lazily loaded on first request instead of during module initialization
// This prevents "insecure operation" errors in development environments

// Guest cart functions
const getGuestCart = () => {
  const guestCart = localStorage.getItem('guestCart');
  return guestCart ? JSON.parse(guestCart) : { items: [], total: 0, shippingCost: 0, grandTotal: 0 };
};

const saveGuestCart = (cart) => {
  localStorage.setItem('guestCart', JSON.stringify(cart));
};

const calculateCartTotals = (items) => {
  const total = items.reduce((sum, item) => {
    // Make sure price is a number
    const price = Number(item.product.price) || 0;
    const quantity = Number(item.quantity) || 0;
    return sum + (price * quantity);
  }, 0);

  const shippingCost = total < 30 ? 5 : 0; // £5 shipping if order value is less than £30
  const grandTotal = total + shippingCost;

  return {
    total: parseFloat(total.toFixed(2)),
    shippingCost: parseFloat(shippingCost.toFixed(2)),
    grandTotal: parseFloat(grandTotal.toFixed(2))
  };
};

// Get cart
export const getCart = async () => {
  try {
    // Try to get cart from server (will use HTTP-only cookies if authenticated)
    const response = await API.get('/api/cart');
    return response.data;
  } catch (error) {
    // If server request fails (user not authenticated), fall back to guest cart
    const guestCart = getGuestCart();
    return { success: true, data: guestCart };
  }
};

// Add to cart
export const addToCart = async (productId, size, quantity = 1, productData = null) => {
  try {
    // Try to add to server cart (will use HTTP-only cookies if authenticated)
    const response = await API.post('/api/cart', { productId, size, quantity });
    return response.data;
  } catch (error) {
    // If server request fails (user not authenticated), add to guest cart
    const guestCart = getGuestCart();

    // Use the product data passed from the product page
    // This avoids making an additional API call
    const product = productData;

    // Check if item already exists in cart
    const existingItemIndex = guestCart.items.findIndex(
      item => item.product._id === productId && item.size === size
    );

    if (existingItemIndex !== -1) {
      // Update quantity if item exists
      guestCart.items[existingItemIndex].quantity += quantity;
    } else {
      // Add new item
      guestCart.items.push({
        _id: Date.now().toString(), // Generate a temporary ID
        product: {
          _id: product._id,
          title: product.title,
          price: product.price,
          images: product.images,
          slug: product.slug,
          sizeVariations: product.sizeVariations,
          category: product.category
        },
        size,
        quantity
      });
    }

    // Calculate totals
    const { total, shippingCost, grandTotal } = calculateCartTotals(guestCart.items);
    guestCart.total = total;
    guestCart.shippingCost = shippingCost;
    guestCart.grandTotal = grandTotal;

    // Save to localStorage
    saveGuestCart(guestCart);

    return { success: true, data: guestCart };
  }
};

// Update cart item
export const updateCartItem = async (itemId, quantity) => {
  try {
    // Try to update server cart (will use HTTP-only cookies if authenticated)
    const response = await API.put(`/api/cart/${itemId}`, { quantity });
    return response.data;
  } catch (error) {
    // If server request fails (user not authenticated), update guest cart
    const guestCart = getGuestCart();

    // Find item in cart
    const itemIndex = guestCart.items.findIndex(item => item._id === itemId);
    if (itemIndex === -1) {
      throw new Error('Item not found in cart');
    }

    // Update quantity
    guestCart.items[itemIndex].quantity = quantity;

    // Calculate totals
    const { total, shippingCost, grandTotal } = calculateCartTotals(guestCart.items);
    guestCart.total = total;
    guestCart.shippingCost = shippingCost;
    guestCart.grandTotal = grandTotal;

    // Save to localStorage
    saveGuestCart(guestCart);

    return { success: true, data: guestCart };
  }
};

// Remove from cart
export const removeFromCart = async (itemId) => {
  try {
    // Try to remove from server cart (will use HTTP-only cookies if authenticated)
    const response = await API.delete(`/api/cart/${itemId}`);
    return response.data;
  } catch (error) {
    // If server request fails (user not authenticated), remove from guest cart
    const guestCart = getGuestCart();

    // Remove item from cart
    guestCart.items = guestCart.items.filter(item => item._id !== itemId);

    // Calculate totals
    const { total, shippingCost, grandTotal } = calculateCartTotals(guestCart.items);
    guestCart.total = total;
    guestCart.shippingCost = shippingCost;
    guestCart.grandTotal = grandTotal;

    // Save to localStorage
    saveGuestCart(guestCart);

    return { success: true, data: guestCart };
  }
};

export const applyCoupon = async (code) => {
  try {
    const response = await API.post('/api/cart/apply-coupon', { code });
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to apply coupon';
  }
};

export const applyMadCoins = async (amount) => {
  try {
    const response = await API.post('/api/cart/apply-madcoins', { amount });
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to apply madcoins';
  }
};

export const removeCoupon = async () => {
  try {
    const response = await API.post('/api/cart/remove-coupon');
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to remove coupon';
  }
};

export const removeGiftCard = async () => {
  try {
    const response = await API.post('/api/cart/remove-gift-card');
    return response.data;
  } catch (error) {
    throw error.response?.data?.error || error.message || 'Failed to remove gift card';
  }
};
